// export default EmiDueReport;
import React, { useEffect, useState } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "../css/EmiDueReport.css";
import { useNavigate } from "react-router-dom";
import { Download, FileTextIcon } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { fetchEmployeeReport } from "../redux/slices/employeeReportSlice";

const EmiDueReport = () => {
  const superAdminId = Number(localStorage.getItem("adminuserId")) || 0;
  const { list: employeeReport } = useSelector((state) => state.employeeReport);
  const dispatch = useDispatch();

  const [emis, setEmis] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filter, setFilter] = useState("pending"); // default filter
  const [company, setCompany] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const navigate = useNavigate();
  const [loanType, setLoanType] = useState("");

  const baseUrl = "https://payyug.in/api/loan-emi";

  const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100, "All"];

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? "N/A" : date.toLocaleDateString("en-GB");
  };

  useEffect(() => {
    dispatch(fetchEmployeeReport({ page: currentPage, superAdminId }));
  }, [dispatch, currentPage, superAdminId]);

  const employeesList = employeeReport?.data || employeeReport || [];

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `https://payyug.in/api/get-company?superAdminId=${superAdminId}`,
        );
        const data = await response.json();

        if (response.ok && data) {
          // Dynamically construct logo URL
          if (data.logo) {
            data.logoUrl = `https://payyug.in/${data.logo.replace(/^images\//, "")}`;
          }
          setCompany(data);
        } else {
          console.error("Failed to fetch company data");
        }
      } catch (err) {
        console.error("Error fetching company details:", err);
      } finally {
        setLoading(false);
      }
    };

    if (superAdminId) fetchCompany();
  }, [navigate, superAdminId]);

  useEffect(() => {
    const superAdminId = Number(localStorage.getItem("adminuserId")) || 0;

    const fetchEmis = async () => {
      setLoading(true);
      setError(null);
      try {
        const endpoints = {
          pending: `${baseUrl}/overdue`,
          today: `${baseUrl}/today`,
          upcoming: `${baseUrl}/upcoming7days`,
        };

        const res = await axios.get(endpoints[filter], {
          params: {
            superAdminId: superAdminId || undefined,
            branchId: selectedEmployee?.id || undefined,
          },
        });
        setEmis(res.data?.emis || []);
      } catch (err) {
        setError("Failed to fetch EMI due report");
        console.error("Fetch EMI Error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchEmis();
  }, [filter, selectedEmployee?.id]);

  const handleItemsPerPageChange = (e) => {
    const val = e.target.value;
    setItemsPerPage(val === "All" ? "All" : Number(val));
    setCurrentPage(1);
  };

const filteredEmis = emis.filter((emi) => {
  const matchesLoanType = loanType
    ? emi.loanType?.toLowerCase() === loanType.toLowerCase()
    : true;

  const emiDateStr = emi.dueDate ? emi.dueDate.slice(0, 10) : "";
  const matchesFromDate = fromDate ? emiDateStr >= fromDate : true;
  const matchesToDate = toDate ? emiDateStr <= toDate : true;

  return matchesLoanType && matchesFromDate && matchesToDate;
});

useEffect(() => {
  setCurrentPage(1);
}, [fromDate, toDate, loanType]);

  const isShowAll = itemsPerPage === "All";
  const effectiveItemsPerPage = isShowAll
    ? filteredEmis.length
    : Number(itemsPerPage);

  const totalPages =
    isShowAll || filteredEmis.length === 0
      ? 1
      : Math.ceil(filteredEmis.length / effectiveItemsPerPage);

  const startIndex = isShowAll ? 0 : (currentPage - 1) * effectiveItemsPerPage;

  const currentData = isShowAll
    ? filteredEmis
    : filteredEmis.slice(startIndex, startIndex + effectiveItemsPerPage);

  const handlePrev = () => currentPage > 1 && setCurrentPage(currentPage - 1);
  const handleNext = () =>
    currentPage < totalPages && setCurrentPage(currentPage + 1);

  const handleDownloadPDF = async () => {
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    // ================= LOGO =================
    const logoX = 15;
    const logoY = 12;
    const logoSize = 25;
    const logoPadding = 2;

    // Border color & thickness
    doc.setDrawColor(0);
    doc.setLineWidth(0.2);

    // Calculate center of logo
    const centerX = logoX + logoSize / 2;
    const centerY = logoY + logoSize / 2;

    // Radius = half of logo + padding
    const radius = logoSize / 2 + logoPadding;

    // Draw circle
    doc.circle(centerX, centerY, radius);

    // Logo image
    if (company?.logoUrl) {
      try {
        const logoResp = await fetch(company.logoUrl);
        const logoBlob = await logoResp.blob();
        const reader = new FileReader();
        await new Promise((resolve) => {
          reader.onload = () => {
            doc.addImage(
              reader.result,
              "PNG",
              logoX,
              logoY,
              logoSize,
              logoSize,
            );
            resolve();
          };
          reader.readAsDataURL(logoBlob);
        });
      } catch {
        // silent fail
      }
    }

    // ================= COMPANY NAME (AUTO WRAP) =================
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);

    const nameX = logoX + logoSize + 5;
    const nameY = 18;
    const nameMaxWidth = pageWidth - nameX - 15;

    const companyNameText = doc.splitTextToSize(
      (company?.name || "COMPANY NAME").toUpperCase(),
      nameMaxWidth,
    );

    doc.text(companyNameText, nameX, nameY);

    // Calculate Y after company name
    let currentY = nameY + companyNameText.length * 6;

    // ================= COMPANY DETAILS =================
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);

    const detailX = nameX;
    const detailMaxWidth = nameMaxWidth;

    // Address
    const addressText = doc.splitTextToSize(
      company?.address || "Company Address",
      detailMaxWidth,
    );
    doc.text(addressText, detailX, currentY);
    currentY += addressText.length * 5;

    // Email
    const emailText = doc.splitTextToSize(
      `Email: ${company?.email || "-"}`,
      detailMaxWidth,
    );
    doc.text(emailText, detailX, currentY);
    currentY += emailText.length * 5;

    // Phone
    const phoneText = doc.splitTextToSize(
      `Phone: ${company?.contact || "-"}`,
      detailMaxWidth,
    );
    doc.text(phoneText, detailX, currentY);
    currentY += phoneText.length * 5;

    // Divider
    doc.line(10, currentY + 4, pageWidth - 10, currentY + 4);

    // ================= REPORT TITLE =================
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text("Emi Due Report", pageWidth / 2, currentY + 14, {
      align: "center",
    });

    // === TABLE ===
    const tableColumn = [
      "#",
      "Customer Name",
      "Loan Number",
      "EMI Number",
      "EMI Amount",
      "Principal",
      "Interest",
      "Outstanding",
      "Due Date",
      "Status",
    ];

    const tableRows = currentData.map((emi, index) => [
      startIndex + index + 1,
      emi.customerName,
      emi.loanNumber,
      emi.emiNumber,
      `Rs. ${emi.emiAmount.toLocaleString("en-GB")}`,
      `Rs. ${emi.principalComponent.toLocaleString("en-GB")}`,
      `Rs. ${emi.interestComponent.toLocaleString("en-GB")}`,
      `Rs. ${emi.outstanding.toLocaleString("en-GB")}`,
      new Date(emi.dueDate).toLocaleDateString("en-GB"),
      emi.status.toUpperCase(),
    ]);

    autoTable(doc, {
      startY: currentY + 20,
      head: [tableColumn],
      body: tableRows,

      theme: "grid",
      styles: {
        fontSize: 7,
        cellPadding: 3,
        halign: "center",
      },
      headStyles: {
        fontStyle: "bold",
        fontSize: 7,
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },

      didDrawPage: function () {
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;

        doc.setDrawColor(0);
        doc.setLineWidth(0.2);

        // Page border on EVERY page
        doc.rect(8, 8, pageWidth - 16, pageHeight - 16);
      },
    });

    // ================= FOOTER =================
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(100);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - 30, pageHeight - 10);
      doc.text("Generated by Loan System", 14, pageHeight - 10);
    }

    doc.save("EMI_Due_Report.pdf");
  };

  const handleDownloadExcel = () => {
    const worksheetData = currentData.map((emi, index) => ({
      "#": startIndex + index + 1,
      "Customer Name": emi.customerName,
      "Loan Number": emi.loanNumber,
      "EMI Number": emi.emiNumber,
      "EMI Amount": emi.emiAmount,
      Principal: emi.principalComponent,
      Interest: emi.interestComponent,
      Outstanding: emi.outstanding,
      "Due Date": new Date(emi.dueDate).toLocaleDateString("en-IN"),
      Status: emi.status,
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "EMI Due Report");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, "EMI_Due_Report.xlsx");
  };

  return (
    <div className="EmiDueReport-wrapper">
      <h1 className="EmiDueReport-title">📋 EMI Due Report</h1>

      {/* Filter buttons */}
      <div className="EmiDueReport-filters">
        {["pending", "today", "upcoming"].map((f) => (
          <button
            key={f}
            className={`filter-btn ${filter === f ? "active" : ""}`}
            onClick={() => {
              setFilter(f);
              setCurrentPage(1);
            }}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
        {/* Loan Type Filter */}
        <select
          className="loan-type-select"
          value={loanType}
          onChange={(e) => {
            setLoanType(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="">All Loan Types</option>
          <option value="Fixed">Fixed</option>
          <option value="Personal">Personal</option>
        </select>

        <select
          className="loan-type-select"
          value={selectedEmployee?.id || ""}
          onChange={(e) => {
            const val = e.target.value;
            const employee = employeesList.find((em) => em.id === Number(val));
            setSelectedEmployee(employee || null);
          }}
        >
          <option value="">Select Employee</option>
          {employeesList.map((e) => (
            <option key={e.id} value={e.id}>
              {e.fullName}
            </option>
          ))}
        </select>
      </div>

      {/* Controls: left rows selector, right PDF download */}
      <div
        className="EmiDueReport-controls"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <label>
          Show{" "}
          <select onChange={handleItemsPerPageChange} value={itemsPerPage}>
            {ITEMS_PER_PAGE_OPTIONS.map((count) => (
              <option key={count} value={count}>
                {count}
              </option>
            ))}
          </select>
          entries
        </label>

        <div className="" style={{ display: "flex", gap: "5px" }}>
          <div className="CollectionReport-formGroup">
            <label>From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
          <div className="CollectionReport-formGroup">
            <label>To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>

        {currentData.length > 0 && (
          <div
            className=""
            style={{ display: "flex", gap: "5px", marginTop: "10px" }}
          >
            <button
              className="collection-report-filter-btn"
              style={{ display: "flex", alignItems: "center", gap: "5px" }}
              onClick={handleDownloadExcel}
            >
              <FileTextIcon size={18} /> Excel
            </button>
            <button
              className="collection-report-filter-btn"
              style={{ display: "flex", alignItems: "center", gap: "5px" }}
              onClick={handleDownloadPDF}
            >
              <Download size={18} /> PDF
            </button>
          </div>
        )}
      </div>

      <div className="EmiDueReport-table-container">
        <table className="EmiDueReport-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Customer Name</th>
              <th>Loan Number</th>
              <th>EMI Number</th>
              <th>EMI Amount</th>
              <th>Principal</th>
              <th>Interest</th>
              <th>Outstanding</th>
              <th>Due Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan="10"
                  className="EmiDueReport-loading"
                  style={{ textAlign: "center", padding: "2rem" }}
                >
                  Loading EMI records...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td
                  colSpan="10"
                  className="EmiDueReport-error"
                  style={{
                    textAlign: "center",
                    padding: "2rem",
                    color: "#dc2626",
                  }}
                >
                  {error}
                </td>
              </tr>
            ) : currentData.length === 0 ? (
              <tr>
                <td colSpan="10" className="EmiDueReport-empty">
                  No records found
                </td>
              </tr>
            ) : (
              currentData.map((emi, index) => (
                <tr key={emi.id}>
                  <td>{startIndex + index + 1}</td>
                  <td>{emi.customerName || "N/A"}</td>
                  <td>{emi.loanNumber || "N/A"}</td>
                  <td>{emi.emiNumber || "N/A"}</td>
                  <td>
                    {emi.emiAmount != null
                      ? `₹${Number(emi.emiAmount).toLocaleString("en-IN")}`
                      : "N/A"}
                  </td>
                  <td>
                    {emi.principalComponent != null
                      ? `₹${Number(emi.principalComponent).toLocaleString("en-IN")}`
                      : "N/A"}
                  </td>
                  <td>
                    {emi.interestComponent != null
                      ? `₹${Number(emi.interestComponent).toLocaleString("en-IN")}`
                      : "N/A"}
                  </td>
                  <td>
                    {emi.outstanding != null
                      ? `₹${Number(emi.outstanding).toLocaleString("en-IN")}`
                      : "N/A"}
                  </td>
                  <td>{formatDate(emi.dueDate)}</td>
                  <td>
                    <span
                      className={`EmiDueReport-status ${emi.status === "pending" ? "pending" : "paid"}`}
                    >
                      {emi.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination controls */}
      <div className="EmiDueReport-pagination">
        <button
          className="pagination-btn"
          onClick={handlePrev}
          disabled={currentPage === 1}
        >
          ⬅ Prev
        </button>
        <span className="pagination-info">
          Page {currentPage} of {totalPages}
        </span>
        <button
          className="pagination-btn"
          onClick={handleNext}
          disabled={currentPage === totalPages}
        >
          Next ➡
        </button>
      </div>
    </div>
  );
};

export default EmiDueReport;
